import { useState } from 'react';

interface VersionUpdate {
  version: string;
  date: string;
  features: string[];
  fixes: string[];
}

const versionHistory: VersionUpdate[] = [
  {
    version: '1.5.0',
    date: '2024-01-15',
    features: [
      'Enhanced security with multi-factor authentication',
      'Improved trading interface with real-time updates',
      'New AI-powered trading bot features',
      'Advanced wallet management system',
      'P2P trading enhancements'
    ],
    fixes: [
      'Fixed transaction processing delays',
      'Resolved wallet sync issues',
      'Improved mobile responsiveness',
      'Enhanced error handling'
    ]
  },
  {
    version: '1.4.0',
    date: '2024-01-01',
    features: [
      'Added PepVerse gaming platform',
      'Introduced staking rewards system',
      'New referral program dashboard',
      'Enhanced KYC verification process'
    ],
    fixes: [
      'Fixed mining session tracking',
      'Resolved payment gateway issues',
      'Improved notification system'
    ]
  },
  {
    version: '1.3.0',
    date: '2023-12-15',
    features: [
      'Launched AI trading robot',
      'Added investment portfolio tracking',
      'New crypto payment gateway',
      'Enhanced security monitoring'
    ],
    fixes: [
      'Fixed authentication bugs',
      'Improved database performance',
      'Resolved UI rendering issues'
    ]
  }
];

export default function VersionInfo() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(versionHistory[0]);

  return (
    <>
      {/* Version Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-40 px-3 py-2 bg-black/80 backdrop-blur-md border border-white/20 rounded-lg text-gray-400 hover:text-white hover:border-cyan-500/50 transition-all duration-300 cursor-pointer group"
      >
        <div className="flex items-center gap-2">
          <i className="ri-information-line text-sm"></i>
          <span className="text-xs font-medium">v{versionHistory[0].version}</span>
          <i className="ri-arrow-up-s-line text-xs group-hover:translate-y-[-2px] transition-transform"></i>
        </div>
      </button>

      {/* Version Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-gray-900 to-black border border-white/20 rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border-b border-white/10 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white text-2xl font-bold mb-1">Version History</h2>
                  <p className="text-gray-400 text-sm">Track all updates and improvements</p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex h-[calc(90vh-120px)]">
              {/* Version List */}
              <div className="w-48 border-r border-white/10 overflow-y-auto">
                {versionHistory.map((version) => (
                  <button
                    key={version.version}
                    onClick={() => setSelectedVersion(version)}
                    className={`w-full px-4 py-3 text-left border-b border-white/5 transition-colors cursor-pointer ${
                      selectedVersion.version === version.version
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="font-semibold text-sm mb-1">v{version.version}</div>
                    <div className="text-xs opacity-70">{version.date}</div>
                  </button>
                ))}
              </div>

              {/* Version Details */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white text-xl font-bold">Version {selectedVersion.version}</h3>
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Latest</span>
                  </div>
                  <p className="text-gray-400 text-sm">Released on {selectedVersion.date}</p>
                </div>

                {/* New Features */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <i className="ri-star-fill text-white text-sm"></i>
                    </div>
                    <h4 className="text-white font-semibold">New Features</h4>
                  </div>
                  <div className="space-y-2">
                    {selectedVersion.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                        <i className="ri-checkbox-circle-fill text-green-400 text-sm mt-0.5"></i>
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bug Fixes */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                      <i className="ri-tools-fill text-white text-sm"></i>
                    </div>
                    <h4 className="text-white font-semibold">Bug Fixes & Improvements</h4>
                  </div>
                  <div className="space-y-2">
                    {selectedVersion.fixes.map((fix, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                        <i className="ri-check-line text-blue-400 text-sm mt-0.5"></i>
                        <span className="text-gray-300 text-sm">{fix}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
